/**
 * OwnershipHistory — Tier 0 slice-local component (SCRUM-172).
 *
 * Reverse-chronological ownership timeline on the Property detail Owner
 * tab. Presentational: renders an ordered list (semantic `<ol>` for
 * screen-reader navigation per ACCESSIBILITY.md) of `OwnershipView`
 * records — owner, share %, effective range, and the ended reason when
 * the record is closed. Empty state when there is no history.
 *
 * Records are sorted newest-first by `effectiveFrom` so the component
 * doesn't depend on BE ordering.
 */

import * as React from 'react';
import { Card } from '@sparta/ui-kit';
import { formatOwnershipDate } from './ownershipDates.js';
import type { OwnershipView } from '../api/types.js';

export interface OwnershipHistoryProps {
  readonly records: readonly OwnershipView[];
}

function effectiveRange(record: OwnershipView): string {
  const from = formatOwnershipDate(record.effectiveFrom);
  const to =
    record.effectiveTo !== undefined
      ? formatOwnershipDate(record.effectiveTo)
      : 'present';
  return `${from} – ${to}`;
}

export function OwnershipHistory({
  records,
}: OwnershipHistoryProps): React.ReactElement {
  const sorted = [...records].sort(
    (a, b) =>
      new Date(b.effectiveFrom).getTime() - new Date(a.effectiveFrom).getTime(),
  );

  return (
    <Card padding="md">
      <h2
        className="m-0 mb-1 text-base font-semibold"
        style={{ color: 'var(--fg-primary)' }}
      >
        Ownership history
      </h2>
      <p
        className="m-0 mb-4 text-sm"
        style={{ color: 'var(--fg-secondary)' }}
      >
        Every ownership record for this property, newest first.
      </p>

      {sorted.length === 0 ? (
        <p
          className="m-0 text-sm italic"
          style={{ color: 'var(--fg-tertiary)' }}
        >
          No ownership history yet.
        </p>
      ) : (
        <ol className="m-0 list-none p-0">
          {sorted.map((record) => (
            <li
              key={record.id}
              className="flex items-start justify-between gap-3 py-3"
              style={{ borderTop: '1px solid var(--border-subtle)' }}
            >
              <div className="flex flex-col gap-0.5">
                <span
                  className="text-sm font-semibold"
                  style={{ color: 'var(--fg-primary)' }}
                >
                  {record.ownerName}
                </span>
                <span
                  className="text-xs"
                  style={{ color: 'var(--fg-secondary)' }}
                >
                  {effectiveRange(record)}
                </span>
                {record.endedReason !== undefined ? (
                  <span
                    className="text-xs"
                    style={{ color: 'var(--fg-tertiary)' }}
                  >
                    Ended: {record.endedReason}
                  </span>
                ) : null}
              </div>
              <span
                className="text-sm font-semibold tabular-nums"
                style={{ color: 'var(--fg-secondary)' }}
              >
                {record.sharePercent}%
              </span>
            </li>
          ))}
        </ol>
      )}
    </Card>
  );
}
